import React, { useEffect, useRef, useState } from "react";
import { Popup } from "react-map-gl";
import { Box, Flex, Heading, VStack, Skeleton, Text } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { Line } from "react-chartjs-2";
import { Trans } from "@lingui/react/macro";

import useAxios from "../../utils/useAxios";
import { DataDump } from "../DeviceComponents/DataDump";
import MiniMoistureGraph from "./MiniMoistureGraph";
import MiniRainGraph from "./MiniRainGraph";
import { IData } from "../../types/deviceInterface";
import { IDevice } from "../../types/deviceInterface";
import { MoistureIndexThresholds } from "../../utils/colorUtils/mapColorUtils";

export interface FlowResponse {
  current: {
    created_at: string;
    flow_l_per_min: number;
    total_pulses: number;
    pulse_duty_cycle: number;
    last_total_pulses: number;
  } | null;
  history: Array<{
    created_at: string;
    flow_l_per_min: number;
  }>;
}

type PopupDevice = (IDevice & { label_code?: string }) | null;

interface Props {
  popupInfo: PopupDevice;
  selectedPlotKey: keyof IData;
  moistureIndexThresholds?: MoistureIndexThresholds;
  onClose: () => void;
  onRendered?: (element: HTMLDivElement | null) => void;
}

const PopupComponent: React.FC<Props> = ({
  popupInfo,
  selectedPlotKey,
  moistureIndexThresholds,
  onClose,
  onRendered,
}) => {
  const api = useAxios();
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [isGraphLoading, setIsGraphLoading] = useState(true);
  const [valveInfo, setValveInfo] = useState<any>(null);
  const [isValveLoading, setIsValveLoading] = useState(false);

  const [flowData, setFlowData] = useState<FlowResponse | null>(null);
  const [isFlowLoading, setIsFlowLoading] = useState(false);

  useEffect(() => {
    if (!popupInfo) return;

    if ((popupInfo.device_type === 5 || popupInfo.device_type === 6) && popupInfo.label_code) {
      setIsValveLoading(true);
      api
        .get(`frontend/valves/label/${popupInfo.label_code}/`)
        .then((res) => setValveInfo(res.data))
        .catch(() => setValveInfo(null))
        .finally(() => setIsValveLoading(false));
    } else {
      setValveInfo(null);
    }
  }, [popupInfo]);

  useEffect(() => {
    if (!popupInfo) return;

    if (popupInfo.device_type === 6) {
      setIsFlowLoading(true);
      api
        .get<FlowResponse>(`frontend/devices/flow-data/${popupInfo.device_id}/`)
        .then((res) => setFlowData(res.data))
        .catch(() => setFlowData(null))
        .finally(() => setIsFlowLoading(false));
    } else {
      setFlowData(null);
    }
  }, [popupInfo]);

  useEffect(() => {
    setIsGraphLoading(true);
  }, [popupInfo?.device_id, popupInfo?.device_type]);

  useEffect(() => {
    if (!contentRef.current || !onRendered) return;

    const notify = () => {
      if (contentRef.current) {
        onRendered(contentRef.current);
      }
    };

    // waut for the first render
    const id = window.requestAnimationFrame(() => {
      notify();
    });

    // on popup change
    const observer = new ResizeObserver(() => {
      notify();
    });

    observer.observe(contentRef.current);

    return () => {
      window.cancelAnimationFrame(id);
      observer.disconnect();
    };
  }, [onRendered, popupInfo, valveInfo, flowData, isGraphLoading]);

  if (!popupInfo) return null;

  return (
    <Popup
      anchor="top"
      longitude={popupInfo.longitude}
      latitude={popupInfo.latitude}
      onClose={onClose}
      maxWidth="none"
      offset={18}
      closeButton
      closeOnClick={true}
    >
      <Box ref={contentRef} w={{ base: "260px", sm: "320px", md: "550px" }}>
        {popupInfo.device_type === 5 ? (
          <Skeleton isLoaded={!isValveLoading} fadeDuration={0.1}>
            {valveInfo ? (
              <VStack align="start" spacing={2} p={2}>
                <Heading size="sm">{valveInfo.device_name || "Unnamed valve"}</Heading>
                <Text fontSize={{ base: "sm", md: "md" }}>
                  <Trans>Temperature:</Trans>{" "}
                  {valveInfo.air_temperature != null ? valveInfo.air_temperature.toFixed(2) : "—"}°C
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }}>
                  <Trans>Humidity:</Trans>{" "}
                  {valveInfo.humidity != null ? valveInfo.humidity.toFixed(2) : "—"}%
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }}>
                  <Trans>Battery:</Trans>{" "}
                  {valveInfo.battery_voltage != null ? valveInfo.battery_voltage.toFixed(2) : "—"} V
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }}>
                  <Trans>State:</Trans> {valveInfo.current_state ?? "Unknown"}
                </Text>
              </VStack>
            ) : (
              <Text color="red.500" p={2}>
                <Trans>Unable to load data</Trans>
              </Text>
            )}
          </Skeleton>
        ) : popupInfo.device_type === 6 ? (
          <Flex p={2}>
            <Flex
              direction={{ base: "column", md: "row" }}
              align="start"
              gap={3}
              p={{ base: 1, md: 2 }}
              w="100%"
            >
              <Box w={{ base: "100%", md: "200px" }}>
                <Link to={`/valve-view/${popupInfo.device_id}`}>
                  <Heading size="sm" mb={2}>
                    {popupInfo.device_name || "Valve"}
                  </Heading>

                  {isValveLoading ? (
                    <Skeleton height="80px" />
                  ) : !valveInfo ? (
                    <Text color="gray.500" fontSize={{ base: "sm", md: "md" }}>
                      <Trans>No valve details available</Trans>
                    </Text>
                  ) : (
                    <VStack align="start" spacing={1} fontSize={{ base: "sm", md: "md" }}>
                      <Text>
                        <Trans>Temperature:</Trans>{" "}
                        {valveInfo.air_temperature != null
                          ? valveInfo.air_temperature.toFixed(2)
                          : "—"}
                        °C
                      </Text>
                      <Text>
                        <Trans>Humidity:</Trans>{" "}
                        {valveInfo.humidity != null ? valveInfo.humidity.toFixed(2) : "—"}%
                      </Text>
                      <Text>
                        <Trans>Battery:</Trans>{" "}
                        {valveInfo.battery_voltage != null
                          ? valveInfo.battery_voltage.toFixed(2)
                          : "—"}{" "}
                        V
                      </Text>
                      <Text>
                        <Trans>State:</Trans> {valveInfo.current_state ?? "Unknown"}
                      </Text>

                      {isFlowLoading ? (
                        <Skeleton height="60px" w="100%" />
                      ) : flowData?.current ? (
                        <>
                          <Text>
                            <Trans>Current flow rate:</Trans>{" "}
                            {flowData.current.flow_l_per_min.toFixed(2)} L/min
                          </Text>
                          <Text>
                            <Trans>Last measured:</Trans>{" "}
                            {new Date(flowData.current.created_at).toLocaleString()}
                          </Text>
                        </>
                      ) : (
                        <Text color="gray.500">
                          <Trans>No flow data</Trans>
                        </Text>
                      )}
                    </VStack>
                  )}
                </Link>
              </Box>

              <Box
                pos="relative"
                w={{ base: "100%", md: "370px" }}
                h={{ base: "180px", md: "200px" }}
              >
                {isFlowLoading ? (
                  <Skeleton height="100%" />
                ) : flowData?.history?.length ? (
                  <Line
                    data={{
                      labels: flowData.history.map((h) => {
                        const d = new Date(h.created_at);
                        const dd = String(d.getDate()).padStart(2, "0");
                        const mm = String(d.getMonth() + 1).padStart(2, "0");
                        return `${dd}.${mm}`;
                      }),
                      datasets: [
                        {
                          label: "Flow (L/min)",
                          data: flowData.history.map((h) => h.flow_l_per_min.toFixed(3)),
                          fill: true,
                          backgroundColor: "rgba(100,150,250,0.2)",
                          borderColor: "rgba(100,150,250,1)",
                          pointRadius: 2,
                          tension: 0.2,
                        },
                      ],
                    }}
                    options={{
                      maintainAspectRatio: false,
                      layout: { padding: { top: 8, bottom: 8, left: 4, right: 4 } },
                      scales: {
                        x: {
                          ticks: {
                            autoSkip: true,
                            maxTicksLimit: 6,
                            maxRotation: 0,
                            padding: 4,
                            font: { size: 10 },
                          },
                          grid: { display: false },
                        },
                        y: {
                          beginAtZero: true,
                          ticks: {
                            autoSkip: true,
                            maxTicksLimit: 4,
                            padding: 4,
                            font: { size: 10 },
                          },
                        },
                      },
                      plugins: {
                        legend: {
                          display: true,
                          position: "top",
                          labels: { boxWidth: 12, padding: 8 },
                        },
                        tooltip: { intersect: false, mode: "index" },
                      },
                    }}
                  />
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    <Trans>No flow history</Trans>
                  </Text>
                )}
              </Box>
            </Flex>
          </Flex>
        ) : popupInfo.device_type === 3 ? (
          <Flex p={2}>
            <Flex direction={{ base: "column", md: "row" }} align="start" gap={3} w="100%">
              <Box w={{ base: "100%", md: "200px" }}>
                <VStack spacing={1} fontSize={13} color="black" align="start">
                  <Heading size={{ base: "sm", md: "md" }}>
                    {popupInfo.device_name ?? (
                      <>
                        <Trans>Device</Trans>
                        {" #"}
                        {popupInfo?.device_id}
                      </>
                    )}
                  </Heading>
                  <Link to={`/devices/${popupInfo.device_id}`}>
                    <DataDump device={popupInfo} />
                  </Link>
                </VStack>
              </Box>

              <Box
                pos="relative"
                w={{ base: "100%", md: "300px" }}
                h={{ base: "180px", md: "200px" }}
                minW={0}
                flexShrink={1}
              >
                <MiniMoistureGraph
                  key={popupInfo.device_id}
                  deviceId={popupInfo.device_id}
                  isGraphloading={isGraphLoading}
                  setIsGraphloading={setIsGraphLoading}
                  plot_key={selectedPlotKey}
                  moistureIndexThresholds={moistureIndexThresholds}
                />
                {isGraphLoading && <Skeleton pos="absolute" top={0} left={0} w="100%" h="100%" />}
              </Box>
            </Flex>
          </Flex>
        ) : popupInfo.device_type === 8 ? (
          <Flex p={2}>
            <Flex direction={{ base: "column", md: "row" }} align="start" gap={3} w="100%">
              <Box w={{ base: "100%", md: "200px" }}>
                <VStack spacing={1} fontSize={13} color="black" align="start">
                  <Heading size={{ base: "sm", md: "md" }}>
                    {popupInfo.device_name ?? (
                      <>
                        <Trans>Device</Trans> {" #"}
                        {popupInfo?.device_id}
                      </>
                    )}
                  </Heading>
                  <Link to={`/weather-station/${popupInfo.device_id}`}>
                    <DataDump device={popupInfo} />
                  </Link>
                </VStack>
              </Box>

              <Box
                pos="relative"
                w={{ base: "100%", md: "300px" }}
                h={{ base: "140px", md: "150px" }}
              >
                <MiniRainGraph
                  key={popupInfo.device_id}
                  deviceId={popupInfo.device_id}
                  isGraphloading={isGraphLoading}
                  setIsGraphloading={setIsGraphLoading}
                />
                {isGraphLoading && <Skeleton pos="absolute" top={0} left={0} w="100%" h="100%" />}
              </Box>
            </Flex>
          </Flex>
        ) : (
          <Flex p={2} flexShrink={0}>
            <Flex direction={{ base: "column", md: "row" }} align="start" gap={3} w="100%">
              <Box w={{ base: "100%", md: "200px" }}>
                <VStack spacing={1} fontSize={13} color="black" align="start">
                  <Heading size={{ base: "sm", md: "md" }}>
                    {popupInfo.device_name ?? (
                      <>
                        <Trans>Device</Trans> {" #"}
                        {popupInfo?.device_id}
                      </>
                    )}
                  </Heading>
                  <Link
                    to={
                      popupInfo.device_type === 9
                        ? `/tracker/${popupInfo.device_id}`
                        : `/devices/${popupInfo.device_id}`
                    }
                  >
                    <DataDump device={popupInfo} />
                  </Link>
                </VStack>
              </Box>

              <Box
                pos="relative"
                w={{ base: "100%", md: "300px" }}
                h={{ base: "180px", md: "200px" }}
                overflow="hidden"
                minW={0}
                flexShrink={1}
              >
                <MiniMoistureGraph
                  key={popupInfo.device_id}
                  deviceId={popupInfo.device_id}
                  isGraphloading={isGraphLoading}
                  setIsGraphloading={setIsGraphLoading}
                  plot_key={selectedPlotKey}
                  moistureIndexThresholds={moistureIndexThresholds}
                />
                {isGraphLoading && <Skeleton pos="absolute" top={0} left={0} w="100%" h="100%" />}
              </Box>
            </Flex>
          </Flex>
        )}
      </Box>
    </Popup>
  );
};

export default PopupComponent;
